import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ChevronDown, Calendar, MapPin, Heart, Sparkles } from "lucide-react";
import { API } from "../App";

export default function LandingPage() {
  const navigate = useNavigate();
  const [weddings, setWeddings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWeddings = async () => {
      try {
        const response = await axios.get(`${API}/weddings`);
        setWeddings(response.data.filter(w => w.status === "active"));
      } catch (error) {
        console.error("Failed to fetch weddings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchWeddings();
  }, []);

  return (
    <div className="min-h-screen bg-wedding-black">
      {/* Hero Section */}
      <section className="hero-section relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: "url('https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=2000&q=80')",
          }}
        />
        <div className="hero-overlay"></div>
        
        <motion.div 
          className="hero-content px-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <p className="text-wedding-gold text-sm tracking-[0.3em] uppercase mb-6">Welcome to</p>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl tracking-tight text-wedding-ivory mb-6">
            Memora<span className="text-wedding-gold">Weddings</span>
          </h1>
          <p className="text-wedding-ivory/70 text-lg md:text-xl max-w-2xl mx-auto mb-12 font-light">
            Preserve your most precious moments in digital eternity. Where memories become timeless treasures.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/login" 
              className="btn-primary inline-block"
              data-testid="get-started-btn"
            >
              Get Started
            </Link>
            <button 
              onClick={() => document.getElementById("weddings")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-secondary inline-flex items-center justify-center gap-2"
              data-testid="explore-btn"
            >
              Explore Weddings
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        <motion.div 
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown className="w-8 h-8 text-wedding-gold/50" />
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-wedding-gold text-sm tracking-[0.3em] uppercase mb-4">Our Features</p>
            <h2 className="section-title">Memories You Can Hold</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: "Wedding Landing Pages",
                description: "Stunning digital spaces to share your love story with family and friends."
              },
              {
                icon: Sparkles,
                title: "Wedding Tree",
                description: "Interactive constellation of heartfelt video messages from your guests."
              },
              {
                icon: Calendar,
                title: "Artifact Tokens",
                description: "Physical keepsakes linked to digital memories through elegant NFC technology."
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="card-hover p-8 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-wedding-gold/10 flex items-center justify-center">
                  <feature.icon className="w-8 h-8 text-wedding-gold" />
                </div>
                <h3 className="font-serif text-xl text-wedding-ivory mb-4">{feature.title}</h3>
                <p className="text-wedding-ivory/60 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Weddings */}
      <section id="weddings" className="py-24 px-6 md:px-12 lg:px-24 bg-wedding-charcoal">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-wedding-gold text-sm tracking-[0.3em] uppercase mb-4">Featured</p>
            <h2 className="section-title">Recent Celebrations</h2>
          </motion.div>

          {loading ? (
            <div className="flex justify-center">
              <div className="w-12 h-12 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : weddings.length === 0 ? (
            <p className="text-center text-wedding-ivory/60">No weddings to display yet.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {weddings.slice(0, 6).map((wedding, index) => (
                <motion.div
                  key={wedding.wedding_id}
                  className="group cursor-pointer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  onClick={() => navigate(`/${wedding.slug}`)}
                  data-testid={`wedding-card-${wedding.slug}`}
                >
                  <div className="aspect-[4/3] overflow-hidden mb-4 bg-wedding-subtle">
                    {wedding.hero_image ? (
                      <img 
                        src={wedding.hero_image.startsWith("http") ? wedding.hero_image : `${process.env.REACT_APP_BACKEND_URL}${wedding.hero_image}`} 
                        alt={`${wedding.couple_name_1} & ${wedding.couple_name_2}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Heart className="w-12 h-12 text-wedding-gold/30" />
                      </div>
                    )}
                  </div>
                  <h3 className="font-serif text-xl text-wedding-ivory group-hover:text-wedding-gold transition-colors duration-300">
                    {wedding.couple_name_1} & {wedding.couple_name_2}
                  </h3>
                  {wedding.wedding_date && (
                    <p className="text-wedding-ivory/50 text-sm mt-1 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {wedding.wedding_date}
                    </p>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-wedding-gold/5 to-transparent"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-4xl md:text-5xl text-wedding-ivory mb-6">
              Begin Your Story
            </h2>
            <p className="text-wedding-ivory/60 text-lg mb-10 max-w-xl mx-auto">
              Create a lasting digital legacy of your wedding day. Every moment preserved, every memory cherished.
            </p>
            <Link 
              to="/login" 
              className="btn-primary inline-block"
              data-testid="cta-get-started"
            >
              Create Your Wedding Page
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto text-center">
          <h3 className="font-serif text-2xl text-wedding-gold mb-2">MemoraOrbs</h3>
          <p className="text-wedding-ivory/40 text-sm italic">Memories You Can Hold</p>
          <div className="mt-8 flex justify-center gap-8">
            <Link to="/login" className="text-wedding-ivory/50 hover:text-wedding-gold text-sm transition-colors duration-200">
              Couple Login
            </Link>
            <Link to="/admin" className="text-wedding-ivory/50 hover:text-wedding-gold text-sm transition-colors duration-200">
              Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
